import React, { useRef } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import CreateAccountForm from "src/customer/create_account_form";
import { createAccount } from "src/customer/create_account";
import { usePhoneVerifiedSubmission } from "src/customer/use_phone_verified_submission";
import VerificationCodeForm from "src/customer/verification_code_form";

const SLIDE_DURATION_MS = 300;

const isScrollable = (element: HTMLElement) => ["auto", "scroll"].includes(window.getComputedStyle(element).overflowY);

const nearestScrollContainer = (element: HTMLElement | null): Element | null => {
  for (let ancestor = element?.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (isScrollable(ancestor)) return ancestor;
  }

  return document.scrollingElement;
}

const SignUp = ({
  afterSignUpCallback,
  signInSelectCallback,
  onClose,
  hasStickyHeader,
}: {
  afterSignUpCallback: () => void;
  signInSelectCallback?: () => void;
  onClose?: () => void;
  hasStickyHeader?: boolean;
}) => {
  const { details, submittedValue, verification } = usePhoneVerifiedSubmission({ submit: createAccount, onSuccess: afterSignUpCallback });
  const isShowingVerifyStep = verification !== null;

  const signUpStepsRef = useRef<HTMLDivElement>(null);
  const detailsStepRef = useRef<HTMLDivElement>(null);
  const verifyStepRef = useRef<HTMLDivElement>(null);

  const currentStepRef = isShowingVerifyStep ? verifyStepRef : detailsStepRef;
  const slideClassNames = isShowingVerifyStep ? "slide-forward" : "slide-back";

  const scrollToTopOfSignUp = () => {
    const scrollContainer = nearestScrollContainer(signUpStepsRef.current);
    if (scrollContainer) scrollContainer.scrollTop = 0;
  }

  const applyCurrentDirectionToLeavingStep = (leavingStep: React.ReactElement) => (
    React.cloneElement(leavingStep, { classNames: slideClassNames } as Partial<React.ComponentProps<typeof CSSTransition>>)
  );

  const verifyStep = verification && <VerificationCodeForm verification={verification} />;

  const detailsStep = (
    <CreateAccountForm
      initialEmail={submittedValue("email")}
      initialPassword={submittedValue("password")}
      initialPasswordConfirmation={submittedValue("password_confirmation")}
      initialFirstName={submittedValue("first_name")}
      initialLastName={submittedValue("last_name")}
      initialPhoneNumber={submittedValue("phone_number")}
      {...details}
      signInSelectCallback={signInSelectCallback}
      onClose={onClose}
      hasStickyHeader={hasStickyHeader}
    />
  );

  return (
    <div ref={signUpStepsRef} className="sign-up-steps" style={{ "--sign-up-step-slide-duration": `${SLIDE_DURATION_MS}ms` } as React.CSSProperties}>
      <TransitionGroup component={null} childFactory={applyCurrentDirectionToLeavingStep}>
        <CSSTransition key={isShowingVerifyStep ? "verify" : "details"} nodeRef={currentStepRef} timeout={SLIDE_DURATION_MS} classNames={slideClassNames} onEnter={scrollToTopOfSignUp}>
          <div ref={currentStepRef}>
            {isShowingVerifyStep ? verifyStep : detailsStep}
          </div>
        </CSSTransition>
      </TransitionGroup>
    </div>
  );
}

export default SignUp;
